import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Users,
  Search,
  Filter,
  MapPin,
  GraduationCap,
  Mail,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  CheckCircle,
  XCircle,
  Loader,
  UserPlus,
  Globe,
  Link2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive list of research fields (alphabetically ordered)
const RESEARCH_FIELDS = [
  'Agricultural Sciences',
  'Anthropology',
  'Archaeology',
  'Architecture',
  'Artificial Intelligence',
  'Astronomy',
  'Biochemistry',
  'Biology',
  'Biomedical Engineering',
  'Biotechnology',
  'Business Administration',
  'Chemical Engineering',
  'Chemistry',
  'Civil Engineering',
  'Climate Science',
  'Communication Studies',
  'Computer Science',
  'Criminology',
  'Data Science',
  'Dentistry',
  'Earth Sciences',
  'Economics',
  'Education',
  'Electrical Engineering',
  'Environmental Science',
  'Finance',
  'Genetics',
  'Geography',
  'Geology',
  'History',
  'Information Technology',
  'International Relations',
  'Law',
  'Linguistics',
  'Literature',
  'Management',
  'Marketing',
  'Materials Science',
  'Mathematics',
  'Mechanical Engineering',
  'Medicine',
  'Microbiology',
  'Molecular Biology',
  'Nanotechnology',
  'Neuroscience',
  'Nursing',
  'Nutrition',
  'Pharmacology',
  'Philosophy',
  'Physics',
  'Political Science',
  'Psychology',
  'Public Health',
  'Robotics',
  'Social Work',
  'Sociology',
  'Software Engineering',
  'Statistics',
  'Veterinary Medicine',
  'Zoology'
].sort();

const TeamMatching = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, isRTL } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [optedIn, setOptedIn] = useState(false);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({
    research_field: 'all',
    user_type: 'all',
    country: 'all',
    collaboration_type: 'all',
    language: 'all'
  });
  const [availableFilters, setAvailableFilters] = useState(null);
  const [searchLimit, setSearchLimit] = useState(20);
  const [stats, setStats] = useState(null);
  const [researcherProfile, setResearcherProfile] = useState(null);
  const [selectedResearcher, setSelectedResearcher] = useState(null);
  const [showOptInDialog, setShowOptInDialog] = useState(false);
  
  // Connection states
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [connectingTo, setConnectingTo] = useState(null);
  const [connectMessage, setConnectMessage] = useState('');
  const [sendingConnection, setSendingConnection] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch researcher profile
      const profileRes = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (profileRes.ok) {
        const profiles = await profileRes.json();
        const myProfile = profiles.find(p => p.email === user.email);
        if (myProfile) {
          setResearcherProfile(myProfile);
          
          // Check opt-in status
          const optInRes = await fetch(
            `${BACKEND_URL}/api/team-matching/opt-in-status/${myProfile.id}`
          );
          if (optInRes.ok) {
            const optInData = await optInRes.json();
            setOptedIn(optInData.matching_enabled);
            
            // If opted in, fetch matches
            if (optInData.matching_enabled) {
              await fetchMatches(myProfile.id);
              await fetchTeams(myProfile.id);
            }
          }
        }
      }
      
      // Fetch available filters
      const filtersRes = await fetch(`${BACKEND_URL}/api/team-matching/available-filters`);
      if (filtersRes.ok) {
        setAvailableFilters(await filtersRes.json());
      }
      
      // Fetch stats
      const statsRes = await fetch(`${BACKEND_URL}/api/team-matching/stats`);
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load team matching data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async (researcherId) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', searchLimit);
      
      if (filters.research_field && filters.research_field !== 'all') params.append('research_field', filters.research_field);
      if (filters.user_type && filters.user_type !== 'all') params.append('user_type', filters.user_type);
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.collaboration_type && filters.collaboration_type !== 'all') params.append('collaboration_type', filters.collaboration_type);
      if (filters.language && filters.language !== 'all') params.append('language', filters.language);
      
      const response = await fetch(
        `${BACKEND_URL}/api/team-matching/recommendations/${researcherId}?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches || []);
      } else {
        throw new Error('Failed to fetch matches');
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast({
        title: "Error",
        description: "Failed to fetch recommendations",
        variant: "destructive"
      });
    }
  };

  const fetchTeams = async (researcherId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/team-matching/team-recommendations/${researcherId}?limit=3&language=${isRTL ? 'ar' : 'en'}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleOptIn = async (enabled) => {
    if (!researcherProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/team-matching/opt-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          researcher_id: researcherProfile.id,
          enabled
        })
      });
      
      if (response.ok) {
        setOptedIn(enabled);
        setShowOptInDialog(false);
        
        toast({
          title: enabled ? "Opted In!" : "Opted Out",
          description: enabled 
            ? "You're now visible for team matching recommendations"
            : "You've been removed from team matching"
        });
        
        if (enabled) {
          await fetchMatches(researcherProfile.id);
        } else {
          setMatches([]);
        }
      }
    } catch (error) {
      console.error('Error updating opt-in:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences",
        variant: "destructive"
      });
    }
  };

  const handleSearch = () => {
    if (researcherProfile && optedIn) {
      fetchMatches(researcherProfile.id);
      fetchTeams(researcherProfile.id);
    }
  };

  const clearFilters = () => {
    setFilters({
      research_field: 'all',
      user_type: 'all',
      country: 'all',
      collaboration_type: 'all',
      language: 'all'
    });
  };

  // Check connection status for a researcher
  const checkConnectionStatus = async (otherResearcherId) => {
    if (!researcherProfile) return null;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/connections/status/${researcherProfile.id}/${otherResearcherId}`
      );
      if (response.ok) {
        const data = await response.json();
        setConnectionStatuses(prev => ({
          ...prev,
          [otherResearcherId]: data.status
        }));
        return data.status;
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
    return null;
  };

  // Fetch connection statuses for all matches
  useEffect(() => {
    if (researcherProfile && matches.length > 0) {
      matches.forEach(match => {
        if (match.researcher_id) {
          checkConnectionStatus(match.researcher_id);
        }
      });
    }
  }, [matches, researcherProfile]);

  // Fetch connection statuses for team members
  useEffect(() => {
    if (researcherProfile && teams.length > 0) {
      teams.forEach(team => {
        team.members.forEach(member => {
          if (member.researcher_id && member.researcher_id !== researcherProfile.id) {
            checkConnectionStatus(member.researcher_id);
          }
        });
      });
    }
  }, [teams, researcherProfile]);

  // Open connect dialog
  const openConnectDialog = (researcher) => {
    setConnectingTo(researcher);
    setConnectMessage('');
    setShowConnectDialog(true);
  };

  // Send connection request
  const sendConnectionRequest = async () => {
    if (!researcherProfile || !connectingTo) return;
    
    setSendingConnection(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_researcher_id: researcherProfile.id,
          to_researcher_id: connectingTo.researcher_id || connectingTo.id,
          message: connectMessage || t('teamMatchingConnectDefaultMessage') || 'I found your profile through team matching and would like to connect for potential research collaboration.'
        })
      });

      if (response.ok) {
        toast({
          title: t('connectionRequestSent') || "Connection Request Sent",
          description: t('requestSentTo') || `Your request has been sent to ${connectingTo.name}`
        });
        setConnectionStatuses(prev => ({
          ...prev,
          [connectingTo.researcher_id || connectingTo.id]: 'pending'
        }));
        setShowConnectDialog(false);
      } else {
        const error = await response.json();
        toast({
          title: t('error') || "Error",
          description: error.detail || t('connectionRequestFailed') || "Failed to send connection request",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending connection request:', error);
      toast({
        title: t('error') || "Error",
        description: t('connectionRequestFailed') || "Failed to send connection request",
        variant: "destructive"
      });
    } finally {
      setSendingConnection(false);
    }
  };

  // Get connect button text and state
  const getConnectButtonState = (researcherId) => {
    const status = connectionStatuses[researcherId];
    if (status === 'accepted') {
      return { text: t('connected') || 'Connected', disabled: true, variant: 'outline' };
    }
    if (status === 'pending') {
      return { text: t('connectionPending') || 'Pending', disabled: true, variant: 'outline' };
    }
    return { text: t('connect') || 'Connect', disabled: false, variant: 'default' };
  };

  // Helper function to translate team strengths and match reasons
  const translateTeamString = (str) => {
    if (!str) return str;
    
    // Team strengths translations
    const strengthsMap = {
      "Diverse academic perspectives": t('diverseAcademicPerspectives'),
      "Interdisciplinary expertise": t('interdisciplinaryExpertise'),
      "Focused domain knowledge": t('focusedDomainKnowledge'),
      "Strong research alignment": t('strongResearchAlignment'),
      "International collaboration": t('internationalCollaboration'),
      "Complementary skills": t('complementarySkills'),
    };
    
    // Match reasons translations
    const reasonsMap = {
      "Strong research interest alignment": t('strongResearchInterestAlignment'),
      "Good research interest match": t('goodResearchInterestMatch'),
      "Local collaboration opportunity": t('localCollaborationOpportunity'),
      "Complementary expertise levels": t('complementaryExpertiseLevels'),
      "Similar research profile": t('similarResearchProfile'),
    };
    
    // Check for exact match first
    if (strengthsMap[str]) return strengthsMap[str];
    if (reasonsMap[str]) return reasonsMap[str];
    
    // Check for patterns like "Same field: X" or "Prefers X collaboration"
    if (str.startsWith("Same field:")) {
      const field = str.replace("Same field:", "").trim();
      return `${t('sameField') || 'Same field'}: ${field}`;
    }
    if (str.includes("collaboration") && str.startsWith("Prefers")) {
      const type = str.replace("Prefers", "").replace("collaboration", "").trim();
      return `${t('prefersCollaboration') || 'Prefers collaboration'} ${type}`;
    }
    
    return str;
  };

  // Translation maps for dropdown values
  const countryTranslations = {
    "Algeria": "الجزائر",
    "Canada": "كندا",
    "Egypt": "مصر",
    "India": "الهند",
    "Iraq": "العراق",
    "Kenya": "كينيا",
    "Libya": "ليبيا",
    "Malaysia": "ماليزيا",
    "Morocco": "المغرب",
    "Nigeria": "نيجيريا",
    "Pakistan": "باكستان",
    "Saudi Arabia": "المملكة العربية السعودية",
    "Sudan": "السودان",
    "Tunisia": "تونس",
    "United Arab Emirates": "الإمارات العربية المتحدة",
    "United Kingdom": "المملكة المتحدة",
    "United States": "الولايات المتحدة",
    "Yemen": "اليمن",
    "Jordan": "الأردن",
    "Lebanon": "لبنان",
    "Syria": "سوريا",
    "Palestine": "فلسطين",
    "Kuwait": "الكويت",
    "Qatar": "قطر",
    "Bahrain": "البحرين",
    "Oman": "عُمان",
    "Australia": "أستراليا",
    "Germany": "ألمانيا",
    "France": "فرنسا",
    "Spain": "إسبانيا",
    "Italy": "إيطاليا",
    "China": "الصين",
    "Japan": "اليابان",
    "South Korea": "كوريا الجنوبية",
    "Indonesia": "إندونيسيا",
    "Turkey": "تركيا",
    "Iran": "إيران",
    "Bangladesh": "بنغلاديش",
    "Philippines": "الفلبين",
    "Vietnam": "فيتنام",
    "Thailand": "تايلاند",
    "South Africa": "جنوب أفريقيا",
    "Brazil": "البرازيل",
    "Mexico": "المكسيك",
    "Argentina": "الأرجنتين",
    "Colombia": "كولومبيا",
    "Chile": "تشيلي",
    "Netherlands": "هولندا",
    "Belgium": "بلجيكا",
    "Sweden": "السويد",
    "Norway": "النرويج",
    "Denmark": "الدنمارك",
    "Finland": "فنلندا",
    "Poland": "بولندا",
    "Russia": "روسيا",
    "Ukraine": "أوكرانيا",
    "Greece": "اليونان",
    "Portugal": "البرتغال",
    "Switzerland": "سويسرا",
    "Austria": "النمسا",
    "New Zealand": "نيوزيلندا",
    "Singapore": "سنغافورة",
  };

  const languageTranslations = {
    "English": "الإنجليزية",
    "Arabic": "العربية",
    "Spanish": "الإسبانية",
    "French": "الفرنسية",
    "German": "الألمانية",
    "Chinese": "الصينية",
    "Japanese": "اليابانية",
    "Korean": "الكورية",
    "Portuguese": "البرتغالية",
    "Russian": "الروسية",
    "Italian": "الإيطالية",
    "Hindi": "الهندية",
    "Urdu": "الأردية",
    "Turkish": "التركية",
    "Persian": "الفارسية",
    "Malay": "الملايوية",
    "Indonesian": "الإندونيسية",
    "Dutch": "الهولندية",
    "Swedish": "السويدية",
    "Polish": "البولندية",
  };

  const academicLevelTranslations = {
    "Both": "كلاهما",
    "Research Assistant": "مساعد باحث",
    "Researcher": "باحث",
    "Professor": "أستاذ",
    "Associate Professor": "أستاذ مشارك",
    "Assistant Professor": "أستاذ مساعد",
    "PhD Student": "طالب دكتوراه",
    "Master Student": "طالب ماجستير",
    "Postdoctoral Researcher": "باحث ما بعد الدكتوراه",
    "Lecturer": "محاضر",
    "Senior Researcher": "باحث أول",
  };

  const researchFieldTranslations = {
    "Agricultural Sciences": "العلوم الزراعية",
    "Anthropology": "علم الإنسان",
    "Archaeology": "علم الآثار",
    "Architecture": "الهندسة المعمارية",
    "Artificial Intelligence": "الذكاء الاصطناعي",
    "Astronomy": "علم الفلك",
    "Biochemistry": "الكيمياء الحيوية",
    "Biology": "علم الأحياء",
    "Biomedical Engineering": "الهندسة الطبية الحيوية",
    "Biotechnology": "التكنولوجيا الحيوية",
    "Business Administration": "إدارة الأعمال",
    "Chemical Engineering": "الهندسة الكيميائية",
    "Chemistry": "الكيمياء",
    "Civil Engineering": "الهندسة المدنية",
    "Climate Science": "علم المناخ",
    "Cognitive Science": "العلوم المعرفية",
    "Communication Studies": "دراسات الاتصال",
    "Computer Science": "علوم الحاسوب",
    "Criminology": "علم الجريمة",
    "Data Science": "علم البيانات",
    "Dentistry": "طب الأسنان",
    "Earth Sciences": "علوم الأرض",
    "Ecology": "علم البيئة",
    "Economics": "الاقتصاد",
    "Education": "التعليم",
    "Electrical Engineering": "الهندسة الكهربائية",
    "Energy Engineering": "هندسة الطاقة",
    "Environmental Science": "العلوم البيئية",
    "Epidemiology": "علم الأوبئة",
    "Finance": "المالية",
    "Food Science": "علوم الغذاء",
    "Genetics": "علم الوراثة",
    "Geography": "الجغرافيا",
    "Geology": "الجيولوجيا",
    "Health Sciences": "العلوم الصحية",
    "History": "التاريخ",
    "Human Resources": "الموارد البشرية",
    "Information Systems": "نظم المعلومات",
    "International Relations": "العلاقات الدولية",
    "Islamic Studies": "الدراسات الإسلامية",
    "Law": "القانون",
    "Library Science": "علم المكتبات",
    "Linguistics": "اللغويات",
    "Literature": "الأدب",
    "Management": "الإدارة",
    "Marine Biology": "الأحياء البحرية",
    "Marketing": "التسويق",
    "Materials Science": "علم المواد",
    "Mathematics": "الرياضيات",
    "Mechanical Engineering": "الهندسة الميكانيكية",
    "Media Studies": "الدراسات الإعلامية",
    "Medicine": "الطب",
    "Microbiology": "علم الأحياء الدقيقة",
    "Music": "الموسيقى",
    "Nanotechnology": "تقنية النانو",
    "Neuroscience": "علم الأعصاب",
    "Nuclear Engineering": "الهندسة النووية",
    "Nursing": "التمريض",
    "Nutrition": "التغذية",
    "Oceanography": "علم المحيطات",
    "Pharmacy": "الصيدلة",
    "Philosophy": "الفلسفة",
    "Physics": "الفيزياء",
    "Political Science": "العلوم السياسية",
    "Psychology": "علم النفس",
    "Public Health": "الصحة العامة",
    "Religious Studies": "الدراسات الدينية",
    "Robotics": "الروبوتات",
    "Social Work": "العمل الاجتماعي",
    "Sociology": "علم الاجتماع",
    "Software Engineering": "هندسة البرمجيات",
    "Statistics": "الإحصاء",
    "Telecommunications": "الاتصالات",
    "Urban Planning": "التخطيط العمراني",
    "Veterinary Medicine": "الطب البيطري",
    "Zoology": "علم الحيوان",
  };

  // Translation helper function for dropdown values
  const translateDropdownValue = (value, type) => {
    if (!isRTL) return value; // Only translate when in Arabic mode
    
    switch(type) {
      case 'country':
        return countryTranslations[value] || value;
      case 'language':
        return languageTranslations[value] || value;
      case 'level':
        return academicLevelTranslations[value] || value;
      case 'field':
        return researchFieldTranslations[value] || value;
      default:
        return value;
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600 bg-green-50';
    if (score >= 0.6) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getMatchScoreLabel = (score) => {
    if (score >= 0.8) return 'Excellent Match';
    if (score >= 0.6) return 'Good Match';
    return 'Potential Match';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-cyan-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading team matching...</p>
        </div>
      </div>
    );
  }

  if (!researcherProfile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">
              Please create a researcher profile to use team matching features.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Breadcrumb / Home Navigation */}
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 -ml-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
          {t('home') || 'Home'}
        </Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('findYourResearchTeamTitle') || 'Find Your Research Team'}
            </h1>
            <p className="text-gray-600">
              {t('aiPoweredMatchingDescription') || 'AI-powered matching to connect you with researchers sharing your interests'}
            </p>
          </div>
          
          <Button
            onClick={() => setShowOptInDialog(true)}
            variant={optedIn ? "outline" : "default"}
            className={optedIn ? "" : "bg-cyan-600 hover:bg-cyan-700"}
          >
            {optedIn ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {t('optedIn') || 'Opted In'}
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                {t('joinMatching') || 'Join Matching'}
              </>
            )}
          </Button>
        </div>
        
        {/* Stats */}
        {stats && (
          <div className="mt-4 flex gap-4">
            <Badge variant="secondary" className="text-sm">
              <Users className="h-3 w-3 mr-1" />
              {stats.total_researchers} {t('totalResearchers') || 'Total Researchers'}
            </Badge>
            <Badge variant="secondary" className="text-sm">
              <Sparkles className="h-3 w-3 mr-1" />
              {stats.opted_in_researchers} {t('activeInMatching') || 'Active in Matching'}
            </Badge>
          </div>
        )}
      </div>

      {!optedIn ? (
        // Opt-in prompt
        <Card className="border-2 border-dashed border-cyan-200">
          <CardContent className="pt-6 text-center py-12">
            <Users className="h-16 w-16 text-cyan-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('joinMatching') || 'Join Team Matching'}</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t('optInDescription') || 'Opt in to discover researchers with similar interests and find your ideal research collaborators. Your profile visibility is controlled by you.'}
            </p>
            <Button
              onClick={() => handleOptIn(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Enable Team Matching
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filters */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                {t('refineYourSearch') || 'Refine Your Search'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Research Field */}
                <Select
                  value={filters.research_field}
                  onValueChange={(value) => setFilters({...filters, research_field: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('researchField') || "Research Field"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">{t('allFields') || 'All Fields'}</SelectItem>
                    {RESEARCH_FIELDS.map(field => (
                      <SelectItem key={field} value={field}>{translateDropdownValue(field, 'field')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Academic Level */}
                <Select
                  value={filters.user_type}
                  onValueChange={(value) => setFilters({...filters, user_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('academicLevel') || "Academic Level"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allLevels') || 'All Levels'}</SelectItem>
                    {availableFilters?.user_types.map(type => (
                      <SelectItem key={type} value={type}>{translateDropdownValue(type, 'level')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Country */}
                <Select
                  value={filters.country}
                  onValueChange={(value) => setFilters({...filters, country: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('country') || "Country"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allCountries') || 'All Countries'}</SelectItem>
                    {availableFilters?.countries.map(country => (
                      <SelectItem key={country} value={country}>{translateDropdownValue(country, 'country')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Collaboration Type */}
                <Select
                  value={filters.collaboration_type}
                  onValueChange={(value) => setFilters({...filters, collaboration_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('collaboration') || "Collaboration"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('anyType') || 'Any Type'}</SelectItem>
                    <SelectItem value="local">{t('local') || 'Local'}</SelectItem>
                    <SelectItem value="international">{t('internationalLabel') || 'International'}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Language */}
                <Select
                  value={filters.language}
                  onValueChange={(value) => setFilters({...filters, language: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('languageFilter') || "Language"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('anyLanguage') || 'Any Language'}</SelectItem>
                    {availableFilters?.languages.map(lang => (
                      <SelectItem key={lang} value={lang}>{translateDropdownValue(lang, 'language')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={handleSearch} className="bg-cyan-600 hover:bg-cyan-700">
                  <Search className="h-4 w-4 mr-2" />
                  {t('searchMatches') || 'Search Matches'}
                </Button>
                <Button onClick={clearFilters} variant="outline">
                  {t('clearFilters') || 'Clear Filters'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recommended Teams Section */}
          {teams.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Users className="h-6 w-6 text-purple-600" />
                {t('recommendedResearchTeamsCount') || 'Recommended Research Teams'} ({teams.length})
              </h2>
              
              <div className="grid grid-cols-1 gap-6">
                {teams.map((team) => (
                  <Card key={team.team_id} className="border-2 border-purple-100 hover:border-purple-300 transition-colors">
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-cyan-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">
                            {t('team') || 'Team'} {team.team_id.split('_')[1]}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {team.members.length} {t('researchersWithSynergy') || 'researchers with'} {team.synergy_score}% {t('synergy') || 'synergy'}
                          </CardDescription>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          {team.synergy_score}% {t('match') || 'Match'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {/* Team Members */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-sm text-gray-700 mb-3">{t('teamMembers') || 'Team Members'}:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {team.members.map((member, idx) => {
                            const isCurrentUser = member.researcher_id === researcherProfile?.id;
                            const connectState = !isCurrentUser ? getConnectButtonState(member.researcher_id) : null;
                            
                            // Translate member roles
                            const translateRole = (role) => {
                              const roleMap = {
                                "Lead Researcher": t('leadResearcher') || "Lead Researcher",
                                "Co-Researcher": t('coResearcher') || "Co-Researcher",
                                "Collaborator": t('collaborator') || "Collaborator",
                              };
                              return roleMap[role] || role;
                            };
                            
                            return (
                              <div key={idx} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-semibold">
                                    {member.name.charAt(0)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{member.name}</p>
                                    <p className="text-xs text-gray-500">{translateRole(member.role)}</p>
                                    {member.research_field && (
                                      <p className="text-xs text-gray-600 mt-1">{member.research_field}</p>
                                    )}
                                  </div>
                                </div>
                                {!isCurrentUser && (
                                  <Button
                                    size="sm"
                                    variant={connectState?.variant || 'default'}
                                    disabled={connectState?.disabled}
                                    className={!connectState?.disabled ? "bg-cyan-600 hover:bg-cyan-700 text-white" : ""}
                                    onClick={() => !connectState?.disabled && openConnectDialog({
                                      researcher_id: member.researcher_id,
                                      name: member.name
                                    })}
                                  >
                                    <Link2 className="h-3 w-3 mr-1" />
                                    {connectState?.text}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Suggested Research Topics */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-purple-600" />
                          {t('suggestedResearchTopics') || 'Suggested Research Topics'}:
                        </h4>
                        <div className="space-y-2">
                          {(team.suggested_research_topics || team.suggested_research_titles || []).map((topic, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 text-purple-700 text-xs flex items-center justify-center font-semibold">
                                {idx + 1}
                              </span>
                              <p className="text-sm text-gray-800 flex-1">{topic}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Team Strengths */}
                      {team.team_strengths && team.team_strengths.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('teamStrengths') || 'Team Strengths'}:</h4>
                          <div className="flex flex-wrap gap-2">
                            {team.team_strengths.map((strength, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                {translateTeamString(strength)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Contact Team Button */}
                      <div className="mt-6">
                        <Button 
                          className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600"
                          onClick={() => {
                            const emails = team.members.slice(1).map(m => m.researcher_id).join(',');
                            toast({
                              title: t('teamContact') || "Team Contact",
                              description: t('contactTeamMembersIndividually') || "Contact team members individually through their profiles"
                            });
                          }}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {t('connectWithTeam') || 'Connect with Team'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Individual Researchers */}
          <div>
            <h2 className="text-2xl font-bold mb-4">
              {t('recommendedResearchers') || 'Recommended Researchers'} ({matches.length})
            </h2>
            
            {matches.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-900 font-medium mb-2">{t('noMatchesFound') || 'No matches found'}</p>
                  <p className="text-gray-600 text-sm mb-4">
                    {stats && stats.opted_in_researchers <= 1 
                      ? t('beFirstToJoin') || "Be one of the first! More researchers are joining the matching system."
                      : t('adjustFilters') || "Try adjusting your filters or check back later as more researchers join."}
                  </p>
                  {stats && (
                    <div className="text-sm text-gray-500 mt-2">
                      <p>{stats.opted_in_researchers} {t('researchersActiveInMatching') || 'researchers currently active in matching'}</p>
                      <p className="mt-1">{t('inviteColleagues') || 'Invite colleagues to join and expand your network!'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {matches.map((match, index) => {
                  const connectState = getConnectButtonState(match.researcher_id);
                  
                  return (
                    <Card key={match.researcher_id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{match.name}</CardTitle>
                            <CardDescription className="mt-1">
                              {match.university}
                            </CardDescription>
                          </div>
                          <Badge className={`${getMatchScoreColor(match.match_score)} border-0`}>
                            {Math.round(match.match_score * 100)}% {t('match') || 'Match'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {/* Details */}
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-2" />
                            {match.country}
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-600">
                            <GraduationCap className="h-4 w-4 mr-2" />
                            {match.user_type}
                          </div>

                          {match.research_field && (
                            <div className="text-sm">
                              <span className="font-medium">{t('field') || 'Field'}:</span> {match.research_field}
                            </div>
                          )}

                          {match.research_interests && (
                            <div className="text-sm">
                              <span className="font-medium">{t('interests') || 'Interests'}:</span>
                              <p className="text-gray-600 mt-1 line-clamp-2">
                                {match.research_interests}
                              </p>
                            </div>
                          )}

                          {/* Match Reasons */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {match.match_reasons.map((reason, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {translateTeamString(reason)}
                              </Badge>
                            ))}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 mt-4 pt-4 border-t">
                            <Button
                              size="sm"
                              className={`flex-1 ${!connectState.disabled ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
                              variant={connectState.variant}
                              disabled={connectState.disabled}
                              onClick={() => !connectState.disabled && openConnectDialog({
                                researcher_id: match.researcher_id,
                                name: match.name,
                                email: match.email
                              })}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              {connectState.text}
                            </Button>
                            
                            {match.google_scholar_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(match.google_scholar_url, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Opt-in Dialog */}
      <Dialog open={showOptInDialog} onOpenChange={setShowOptInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('teamMatchingSettings') || 'Team Matching Settings'}</DialogTitle>
            <DialogDescription>
              {t('controlVisibility') || 'Control your visibility in the team matching system'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{t('privacy') || 'Privacy'}:</strong> {t('privacyNote') || 'Your profile is only visible to other opted-in researchers.'}
                {' '}{t('canOptOutAnytime') || 'You can opt out at any time.'}
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {t('currentStatus') || 'Current Status'}: {optedIn ? (
                  <span className="text-green-600 font-medium">{t('optedIn') || 'Opted In'}</span>
                ) : (
                  <span className="text-gray-500">{t('notOptedIn') || 'Not Opted In'}</span>
                )}
              </p>
            </div>
          </div>

          <DialogFooter>
            {optedIn ? (
              <Button
                variant="outline"
                onClick={() => handleOptIn(false)}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t('optOut') || 'Opt Out'}
              </Button>
            ) : (
              <Button
                onClick={() => handleOptIn(true)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {t('optIn') || 'Opt In'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connect Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-cyan-600" />
              {t('connectWith') || 'Connect with'} {connectingTo?.name}
            </DialogTitle>
            <DialogDescription>
              {t('sendConnectionRequestMessage') || 'Send a connection request to start collaborating'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('message') || 'Message'} ({t('optional') || 'Optional'})
              </label>
              <Textarea
                value={connectMessage}
                onChange={(e) => setConnectMessage(e.target.value)}
                placeholder={t('connectMessagePlaceholder') || "Hi, I found your profile through team matching and would like to connect for potential research collaboration..."}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConnectDialog(false)}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={sendConnectionRequest}
              disabled={sendingConnection}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {sendingConnection ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  {t('sending') || 'Sending...'}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('sendRequest') || 'Send Request'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMatching;
